import React, { useEffect, useMemo, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';

import styled from '@emotion/styled';

import { FontWeight } from '@clutter/clean';

import { WWW_ROUTES } from '@root/root/routes';

import { accountBaseUrl } from '@utils/environment_urls';

import eyeIcon from '@images/checkout/challenger/eye.svg';
import lockPerson from '@images/checkout/challenger/lock_person.svg';
import mailIcon from '@images/checkout/challenger/mail.svg';

import { useChallengerBreakpoints } from './hooks/use_challenger_breakpoints';
import { ChallengerLayout } from './layout';
import { TABLET_UP as DESKTOP, MOBILE } from './media';
import { ContentColumn, MainRow, Page, SummaryColumn } from './shared';
import { CHALLENGER as T } from './tokens';

export type SetPasswordLocationState = {
  accountUrl: string;
  email?: string;
  addressLine?: string;
  appointmentLabel?: string;
};

const REQUIREMENTS = [
  {
    id: 'lower',
    label: 'A lowercase letter',
    test: (v: string) => /[a-z]/.test(v),
  },
  {
    id: 'upper',
    label: 'An uppercase letter',
    test: (v: string) => /[A-Z]/.test(v),
  },
  {
    id: 'number',
    label: 'A number',
    test: (v: string) => /\d/.test(v),
  },
  {
    id: 'symbol',
    label: 'A symbol (e.g. !@#$)',
    test: (v: string) => /[^A-Za-z0-9]/.test(v),
  },
  {
    id: 'length',
    label: 'At least 12 characters',
    test: (v: string) => v.length >= 12,
  },
] as const;

const SET_PASSWORD_SUBMIT_ERROR =
  'Unable to continue. Please try reserving again.';

const SET_PASSWORD_REQUEST_TIMEOUT_MS = 30_000;

/** Match portal URL protocol/origin to configured account base (http locally, https deployed). */
const isTrustedAccountPortalUrl = (portalUrl: URL): boolean => {
  let expectedPortal: URL;
  try {
    expectedPortal = new URL(accountBaseUrl());
  } catch {
    return false;
  }

  return (
    portalUrl.protocol === expectedPortal.protocol &&
    portalUrl.origin === expectedPortal.origin
  );
};

const Title = styled.h1`
  margin: 0;
  font-weight: ${FontWeight.Medium};
  color: ${T.color.textPrimary};

  ${MOBILE} {
    font-size: 16px;
    line-height: 22px;
    color: #01252d;
  }

  ${DESKTOP} {
    font-size: 20px;
    line-height: 24px;
  }
`;

const FormBlock = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;

  ${MOBILE} {
    gap: ${T.space[16]};
  }
`;

/** Figma 707:16395 mobile / 707:15157 desktop — email confirmation banner. */
const EmailBar = styled.div`
  display: flex;
  align-items: flex-start;
  gap: ${T.space[8]};
  width: 100%;
  padding: ${T.space[12]} ${T.space[16]};
  margin: 0;
  box-sizing: border-box;
  background: ${T.color.summarySurface};
  border-radius: ${T.radius.md};
  color: ${T.color.textPrimary};

  ${DESKTOP} {
    align-items: center;
    margin: ${T.space[24]} 0;
    font-size: 14px;
    line-height: 20px;
  }
`;

const EmailCopy = styled.div`
  display: flex;
  min-width: 0;

  ${MOBILE} {
    flex: 1 1 auto;
    flex-direction: column;
    align-items: flex-start;
    justify-content: center;
    word-break: break-word;
  }

  ${DESKTOP} {
    flex-direction: row;
    align-items: center;
    white-space: nowrap;
  }
`;

/** CSS breakpoint swap — avoids `isDesktop` showing desktop copy on mobile/tablet. */
const DesktopEmailCopy = styled(EmailCopy)`
  display: none;

  ${DESKTOP} {
    display: flex;
  }
`;

const MobileEmailCopy = styled(EmailCopy)`
  ${DESKTOP} {
    display: none;
  }
`;

const EmailText = styled.div`
  font-family: ${T.font.sans};
  font-size: 14px;
  line-height: 20px;
  font-weight: ${FontWeight.Regular};
`;

/** Figma 707:15162 — inline medium email on desktop. */
const EmailInline = styled.span`
  font-weight: ${FontWeight.Medium};
  line-height: 21px;
`;

/** Figma 707:16400 — medium email on its own line on mobile. */
const EmailId = styled.div`
  font-family: ${T.font.sans};
  font-size: 13px;
  line-height: 18px;
  font-weight: ${FontWeight.Medium};
  width: 100%;
`;
/**
 * Figma 707:15158 — icon-fill-brand-25 shell + 20×20 mail_outline slot.
 * Leaf inset 16.67% / 8.33% (16.67×13.33), not stretched to 20×20.
 */
const MailBadge = styled.span`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  flex-shrink: 0;
  padding: 4px;
  border-radius: ${T.radius.sm};
  background: rgba(11, 122, 122, 0.25);

  ${DESKTOP} {
    display: inline-flex;
    flex-direction: row;
    background: rgba(8, 170, 168, 0.25);
  }
`;

const MailIconBox = styled.span`
  position: relative;
  display: block;
  width: 20px;
  height: 20px;
  overflow: hidden;
  flex-shrink: 0;
`;

const MailImg = styled.img`
  position: absolute;
  top: 3.33px;
  left: 1.67px;
  width: 16.67px;
  height: 13.33px;
  display: block;
`;

const Card = styled.div`
  background: ${T.color.bgPrimary};
  border-radius: ${T.radius.md};
  width: 100%;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;

  ${MOBILE} {
    padding: ${T.space[16]};
    gap: ${T.space[16]};
  }

  ${DESKTOP} {
    padding: ${T.space[24]};
    gap: ${T.space[24]};
  }
`;

const CardTitle = styled.h2`
  margin: 0;
  font-weight: ${FontWeight.Medium};
  color: ${T.color.textPrimary};

  ${MOBILE} {
    font-size: 14px;
    line-height: 20px;
    color: #01252d;
  }

  ${DESKTOP} {
    font-size: 16px;
    line-height: 24px;
  }
`;

/** Figma 707:15147 / 707:19965 — password field with floating label on focus/fill. */
const InputWrap = styled.div<{ $focused?: boolean; $raised?: boolean }>`
  position: relative;
  width: 100%;
  border: 1px solid
    ${({ $focused }) => ($focused ? T.color.iconBrand : T.color.borderDefault)};
  border-radius: ${T.radius.sm};
  background: ${T.color.bgPrimary};
  box-sizing: border-box;
`;

const FloatLabel = styled.span<{ $focused?: boolean; $raised?: boolean }>`
  position: absolute;
  left: 11px;
  top: -11px;
  z-index: 1;
  padding: 2px 4px;
  background: ${T.color.bgPrimary};
  font-family: ${T.font.sans};
  font-size: 12px;
  font-weight: ${FontWeight.Medium};
  line-height: normal;
  color: ${({ $focused }) =>
    $focused ? T.color.textBrandDark : T.color.textTertiary};
  pointer-events: none;
  opacity: ${({ $raised }) => ($raised ? 1 : 0)};
  transition: opacity 0.12s ease;
`;

const Input = styled.input`
  appearance: none;
  width: 100%;
  height: 54px;
  padding: 0 48px 0 ${T.space[16]};
  border: none;
  border-radius: ${T.radius.sm};
  background: transparent;
  font-family: ${T.font.sans};
  font-size: 16px;
  line-height: 24px;
  color: ${T.color.textPrimary};
  box-sizing: border-box;

  &::placeholder {
    color: ${T.color.textTertiary};
  }

  &:focus {
    outline: none;
  }

  ${MOBILE} {
    font-size: 14px;
    line-height: 20px;
    letter-spacing: -0.3px;

    /* iOS Safari zooms inputs < 16px; Android can keep Figma 14px. */
    @supports (-webkit-touch-callout: none) {
      font-size: 16px;
      line-height: 24px;
      letter-spacing: normal;
    }
  }
`;

/**
 * Figma 707:15167 — eye sits in the 16px padded input row (not 12px + 4px pad).
 */
const EyeButton = styled.button`
  appearance: none;
  position: absolute;
  right: ${T.space[16]};
  top: 50%;
  transform: translateY(-50%);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: none;
  background: none;
  padding: 0;
  cursor: pointer;
`;

/** Figma icon/eye_open — 20×20 slot; leaf inset 20% / 3.33% / 17.5% / 5%. */
const EyeIconBox = styled.span`
  position: relative;
  display: block;
  width: 20px;
  height: 20px;
  overflow: hidden;
  flex-shrink: 0;
`;

const EyeImg = styled.img`
  position: absolute;
  top: 4px;
  left: 1px;
  width: 18.33px;
  height: 12.5px;
  display: block;
`;

/**
 * Figma 707:15166 — Input component is 74px (54px field + supporting space),
 * then 2px gap before the “Password must contain” helper.
 */
const PasswordFieldBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  width: 100%;
`;

const InputSlot = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 74px;
`;

const Rules = styled.div`
  padding: 13px ${T.space[16]};
  border-radius: ${T.radius.md};
  background: ${T.color.bgPage};
`;

const RulesTitle = styled.div`
  font-size: 14px;
  font-weight: ${FontWeight.Medium};
  line-height: 21px;
  color: ${T.color.textPrimary};
  margin-bottom: ${T.space[8]};
`;

/**
 * Figma 707:15169 unmet — label #1F2F38, tick #9AA1A5.
 * Figma 707:19991 met — label #0B7A7A, tick #08AAA8.
 * Hexes applied inline on path/label so CSS inheritance cannot override them.
 */
const RULE_TICK_UNMET = '#9AA1A5';
const RULE_TICK_MET = '#08AAA8';
const RULE_LABEL_UNMET = '#1F2F38';
const RULE_LABEL_MET = '#0B7A7A';

const RuleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 14px;
  font-weight: ${FontWeight.Medium};
  line-height: 21px;
`;

const RuleLabel = styled.span<{ met: boolean }>`
  color: ${({ met }) => (met ? RULE_LABEL_MET : RULE_LABEL_UNMET)};
`;

const CheckIcon = styled.svg`
  width: 16px;
  height: 16px;
  flex-shrink: 0;
  display: block;
  overflow: visible;
`;

const Submit = styled.button<{ disabled?: boolean }>`
  appearance: none;
  align-self: flex-start;
  height: 48px;
  margin-top: ${T.space[8]};
  padding: 0 ${T.space[24]};
  border: none;
  border-radius: ${T.radius.md};
  cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
  background: ${({ disabled }) =>
    disabled ? T.color.bgButtonDisabled : T.color.textBrand};
  color: ${({ disabled }) =>
    disabled ? T.color.textDisabled : T.color.textInverse};
  font-family: ${T.font.sans};
  font-weight: ${FontWeight.Semibold};

  ${MOBILE} {
    font-size: 14px;
    line-height: 12px;
  }

  ${DESKTOP} {
    font-size: 16px;
    line-height: 16px;
  }

  &:hover {
    opacity: ${({ disabled }) => (disabled ? 1 : 0.92)};
  }
`;

const ReadyCard = styled.div`
  width: ${T.layout.summaryWidth};
  background: ${T.color.summarySurface};
  border-radius: ${T.radius.md};
  padding: ${T.space[24]} ${T.space[16]} ${T.space[16]};
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${T.space[32]};
`;

const LockImg = styled.img`
  width: 112px;
  height: 112px;
  display: block;
  object-fit: contain;
`;

const ReadyCopy = styled.div`
  width: 100%;
  text-align: center;
`;

const ReadyTitle = styled.h2`
  margin: 0 0 ${T.space[8]};
  font-size: 20px;
  font-weight: ${FontWeight.Medium};
  line-height: 24px;
  color: ${T.color.textSecondary};
`;

const ReadyBody = styled.p`
  margin: 0;
  font-size: 14px;
  line-height: 20px;
  color: ${T.color.textPrimary};
`;

const PickupBox = styled.div`
  width: 100%;
  padding: ${T.space[12]};
  box-sizing: border-box;
  border-radius: ${T.radius.md};
  background: ${T.color.timelineSurface};
  border: 1px solid rgba(198, 170, 119, 0.4);
  color: ${T.color.textSecondary};
  font-size: 14px;
  line-height: 20px;
`;

const PickupLabel = styled.div`
  font-weight: ${FontWeight.Semibold};
  line-height: 19px;
  margin-bottom: 4px;
`;

/** Mobile-only ready card under the form (Figma 707:16390). */
const MobileReady = styled.div`
  display: none;

  ${MOBILE} {
    display: flex;
    flex-direction: column;
    gap: ${T.space[24]};
    width: 100%;
    margin-top: ${T.space[24]};
    padding: ${T.space[16]};
    box-sizing: border-box;
    border-radius: ${T.radius.md};
    background: ${T.color.summarySurface};
  }
`;

const MobileReadyTop = styled.div`
  display: flex;
  gap: ${T.space[8]};
  align-items: flex-start;
  width: 100%;
`;

const MobileLockImg = styled.img`
  width: 48px;
  height: 48px;
  display: block;
  flex-shrink: 0;
  object-fit: contain;
`;

const MobileReadyCopy = styled.div`
  flex: 1 1 auto;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const MobileReadyTitle = styled.h2`
  margin: 0;
  font-size: 16px;
  font-weight: ${FontWeight.Medium};
  line-height: 22px;
  color: ${T.color.textSecondary};
`;

/** Figma 707:16390 / 913:37502 — Body/Medium 400 Mobile 14/20. */
const MobileReadyBody = styled.p`
  margin: 0;
  font-size: 14px;
  line-height: 20px;
  color: ${T.color.textPrimary};
`;

/**
 * Figma 913:37504 — tan pickup card; content stack gap 4 under
 * “Pickup is booked for” (913:37505).
 */
const MobilePickupBox = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${T.space[4]};
  width: 100%;
  padding: ${T.space[12]};
  box-sizing: border-box;
  border-radius: ${T.radius.md};
  background: ${T.color.timelineSurface};
  border: 1px solid rgba(198, 170, 119, 0.4);
  color: ${T.color.textSecondary};
  font-size: 14px;
  font-weight: ${FontWeight.Regular};
  line-height: 20px;
`;

const MobilePickupLabel = styled.div`
  font-size: 13px;
  font-weight: ${FontWeight.Semibold};
  line-height: 16px;
`;

const PasswordMainRow = styled(MainRow)`
  ${MOBILE} {
    padding-bottom: ${T.space[48]};
  }
`;

const SubmitError = styled.p`
  margin: 0;
  font-size: 14px;
  line-height: 20px;
  color: ${T.color.textError};
`;

export const ChallengerSetPasswordPage: React.FC = () => {
  const history = useHistory();
  const location = useLocation<SetPasswordLocationState | undefined>();
  const { isMobile } = useChallengerBreakpoints();
  const state = location.state;
  const accountUrl = state?.accountUrl;
  const submitLabel = isMobile ? 'Sign up' : 'Submit';

  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [passwordFocused, setPasswordFocused] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string>();

  const results = useMemo(
    () => REQUIREMENTS.map((r) => ({ ...r, met: r.test(password) })),
    [password],
  );
  const allMet = results.every((r) => r.met);
  const passwordRaised = passwordFocused || password.length > 0;

  useEffect(() => {
    if (!accountUrl) {
      history.replace(WWW_ROUTES.PRODUCT_PAGE_STORAGE);
    }
  }, [accountUrl, history]);

  if (!accountUrl || !state) {
    return null;
  }

  /**
   * Challenger set-password stays on landing; POST account challenger_initial_setup/password
   * (JSON) then redirect to estimation_flow on account.*.
   */
  const onSubmit = async () => {
    if (!allMet || submitting) return;

    let portalUrl: URL;
    try {
      portalUrl = new URL(accountUrl);
    } catch {
      setSubmitError(SET_PASSWORD_SUBMIT_ERROR);
      return;
    }

    if (!isTrustedAccountPortalUrl(portalUrl)) {
      setSubmitError(SET_PASSWORD_SUBMIT_ERROR);
      return;
    }

    const token = portalUrl.searchParams.get('initial_setup_token');
    if (!token) {
      setSubmitError(SET_PASSWORD_SUBMIT_ERROR);
      return;
    }

    setSubmitError(undefined);
    setSubmitting(true);

    const controller = new AbortController();
    const timeoutId = window.setTimeout(
      () => controller.abort(),
      SET_PASSWORD_REQUEST_TIMEOUT_MS,
    );

    try {
      const response = await fetch(
        `${portalUrl.origin}/challenger_initial_setup/password`,
        {
          method: 'POST',
          credentials: 'include',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            initial_setup_token: token,
            password,
          }),
          signal: controller.signal,
        },
      );

      const body: {
        redirect_url?: string;
        error?: string;
        errors?: Record<string, string[]>;
      } = await response.json().catch(() => ({}));

      if (response.ok && body.redirect_url) {
        try {
          const redirectUrl = new URL(body.redirect_url);
          if (isTrustedAccountPortalUrl(redirectUrl)) {
            window.location.href = body.redirect_url;
            return;
          }
        } catch {
          // Fall through to generic error handling.
        }
      }

      if (typeof body.error === 'string' && body.error) {
        setSubmitError(body.error);
      } else if (body.errors?.password?.[0]) {
        setSubmitError(body.errors.password[0]);
      } else {
        setSubmitError(SET_PASSWORD_SUBMIT_ERROR);
      }
      setSubmitting(false);
    } catch {
      setSubmitError(SET_PASSWORD_SUBMIT_ERROR);
      setSubmitting(false);
    } finally {
      window.clearTimeout(timeoutId);
    }
  };

  const pickupBlock = (state.addressLine || state.appointmentLabel) && (
    <>
      {state.addressLine && <div>{state.addressLine}</div>}
      {state.appointmentLabel && <div>{state.appointmentLabel}</div>}
    </>
  );

  return (
    <ChallengerLayout navVariant="password">
      <Page>
        <PasswordMainRow>
          <ContentColumn>
            <FormBlock>
              <Title>Set a password to continue</Title>
              <EmailBar>
                <MailBadge>
                  <MailIconBox>
                    <MailImg src={mailIcon} alt="" />
                  </MailIconBox>
                </MailBadge>
                <DesktopEmailCopy>
                  <EmailText>
                    Your account was created with{' '}
                    <EmailInline>{state.email || 'your email'}.</EmailInline>
                  </EmailText>
                </DesktopEmailCopy>
                <MobileEmailCopy>
                  <EmailText>Your account was created with email</EmailText>
                  <EmailId>{state.email || 'your email'}.</EmailId>
                </MobileEmailCopy>
              </EmailBar>
              <Card>
                <CardTitle>Set password</CardTitle>
                <div>
                  <PasswordFieldBlock>
                    <InputSlot>
                      <InputWrap
                        $focused={passwordFocused}
                        $raised={passwordRaised}
                      >
                        <FloatLabel
                          $focused={passwordFocused}
                          $raised={passwordRaised}
                        >
                          Password
                        </FloatLabel>
                        <Input
                          type={showPassword ? 'text' : 'password'}
                          placeholder={passwordRaised ? '' : 'Password'}
                          value={password}
                          onChange={(e) => setPassword(e.target.value)}
                          onFocus={() => setPasswordFocused(true)}
                          onBlur={() => setPasswordFocused(false)}
                          autoComplete="new-password"
                          aria-label="Password"
                        />
                        <EyeButton
                          type="button"
                          aria-label={
                            showPassword ? 'Hide password' : 'Show password'
                          }
                          onClick={() => setShowPassword((v) => !v)}
                        >
                          <EyeIconBox>
                            <EyeImg src={eyeIcon} alt="" />
                          </EyeIconBox>
                        </EyeButton>
                      </InputWrap>
                    </InputSlot>
                    <Rules>
                      <RulesTitle>Password must contain</RulesTitle>
                      {results.map((r) => (
                        <RuleRow key={r.id}>
                          <CheckIcon viewBox="0 0 16 16" aria-hidden>
                            <path
                              d="M14 4.66656L6 12.6666L2.33333 8.9999L3.27333 8.0599L6 10.7799L13.06 3.72656L14 4.66656Z"
                              fill={r.met ? RULE_TICK_MET : RULE_TICK_UNMET}
                            />
                          </CheckIcon>
                          <RuleLabel met={r.met}>{r.label}</RuleLabel>
                        </RuleRow>
                      ))}
                    </Rules>
                  </PasswordFieldBlock>
                </div>
                {submitError && (
                  <SubmitError role="alert">{submitError}</SubmitError>
                )}
                <Submit
                  type="button"
                  disabled={!allMet || submitting}
                  onClick={onSubmit}
                >
                  {submitLabel}
                </Submit>
              </Card>
            </FormBlock>

            <MobileReady>
              <MobileReadyTop>
                <MobileLockImg src={lockPerson} alt="" />
                <MobileReadyCopy>
                  <MobileReadyTitle>
                    Your storage space is ready
                  </MobileReadyTitle>
                  <MobileReadyBody>
                    Set a password to track inventory and manage your pickup
                    details.
                  </MobileReadyBody>
                </MobileReadyCopy>
              </MobileReadyTop>
              {(state.addressLine || state.appointmentLabel) && (
                <MobilePickupBox>
                  <MobilePickupLabel>Pickup is booked for</MobilePickupLabel>
                  {pickupBlock}
                </MobilePickupBox>
              )}
            </MobileReady>
          </ContentColumn>
          <SummaryColumn>
            <ReadyCard>
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: 24,
                  width: '100%',
                }}
              >
                <LockImg src={lockPerson} alt="" />
                <ReadyCopy>
                  <ReadyTitle>Your storage space is ready</ReadyTitle>
                  <ReadyBody>
                    Set a password to manage your storage plan and pickup
                    details.
                  </ReadyBody>
                </ReadyCopy>
              </div>
              {(state.addressLine || state.appointmentLabel) && (
                <PickupBox>
                  <PickupLabel>Pickup is booked for</PickupLabel>
                  {pickupBlock}
                </PickupBox>
              )}
            </ReadyCard>
          </SummaryColumn>
        </PasswordMainRow>
      </Page>
    </ChallengerLayout>
  );
};
